import { toE11Error } from '@engineering11/web-api-error';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { isObservable, of, from } from 'rxjs';
import { take, switchMap } from 'rxjs/operators';

function isE11ApiError(error) {
    return !!error?.response?.data?.type;
}

var VersionType;
(function (VersionType) {
    VersionType["Header"] = "Header";
    VersionType["URI"] = "URI";
})(VersionType || (VersionType = {}));
const defaultRequestOptions = {
    version: '1',
    responseType: 'json',
};
class RestApiClient {
    constructor(options) {
        this.config = {};
        this.axiosInstance = axios.create();
        this.counter = 0;
        axiosRetry(this.axiosInstance, {
            retryCondition: axiosRetry.isSafeRequestError, // [GET, HEAD, OPTIONS] + 500s
            retries: options.retries,
            retryDelay: axiosRetry.exponentialDelay,
            onRetry: (retryCount, error) => {
                const logMessage = `Retrying: ${retryCount}; Error: ${error}`;
                if (options.logger) {
                    options.logger.error(logMessage);
                }
                else {
                    console.error(logMessage);
                }
            },
        });
        const token = options.token;
        if (isObservable(token)) {
            this.token$ = token.pipe(take(1)); // ! verify that there is no way REST calls are re-run by a rogue observable emitting more than once
        }
        else if (typeof token === 'string') {
            this.token$ = of(token);
        }
        else {
            // TODO: find a suitable way to convert this to an observable without bugs - test thoroughly
            this.token$ = token;
            // this.token$ = defer(async () => token()) // Defer ensures this function is called on subscribe http://reactivex.io/rxjs/class/es6/Observable.js~Observable.html#static-method-defer
        }
        this.config.baseURL = options.baseUrl;
        const throwNon200s = options.throwNon200s || true;
        if (throwNon200s) {
            this.config.validateStatus = (status) => status >= 200 && status < 300;
        }
        else {
            this.config.validateStatus = (status) => status < 500;
        }
        this.versionType = options.versionType ?? VersionType.URI;
        this.axiosInstance.interceptors.response.use((response) => {
            return response;
        }, (error) => {
            delete error.config.headers['Authorization'];
            delete error.config.headers['X-TENANT-ID'];
            delete error.request['_header'];
            if (isE11ApiError(error)) {
                const errorData = { ...error.response.data, source: 'rest-api' };
                return Promise.reject(toE11Error(error, errorData));
            }
            else {
                /**
                 * If not an E11 error, we need to let it be handled as such rather than forcing it to be an E11 error
                 * Otherwise our global error mapper system has no way of knowing it needs to convert it
                 * TODO: Consider allowing mappers to be passed into the RestApiClient OR creating an Injectable Rest API Client that can inject the mapping system and intercept errors itself
                 */
                return Promise.reject(error);
            }
        });
    }
    get(path, options = {}) {
        const requestOptions = { ...defaultRequestOptions, ...options };
        const requestPath = this.versionType === VersionType.URI ? this.versionUrl(requestOptions.version, path) : path;
        const getFn = (token) => this.axiosInstance.get(requestPath, this.buildConfig(requestOptions, token));
        // TODO: refactor when this.token$ is reliably an observable
        return this.runFnWithToken(getFn);
    }
    put(path, body, options = {}) {
        const requestOptions = { ...defaultRequestOptions, ...options };
        const requestPath = this.versionType === VersionType.URI ? this.versionUrl(requestOptions.version, path) : path;
        const putFn = (token) => this.axiosInstance.put(requestPath, body, this.buildConfig(requestOptions, token));
        return this.runFnWithToken(putFn);
    }
    post(path, body, options = {}) {
        const requestOptions = { ...defaultRequestOptions, ...options };
        const requestPath = this.versionType === VersionType.URI ? this.versionUrl(requestOptions.version, path) : path;
        const postFn = (token) => this.axiosInstance.post(requestPath, body, this.buildConfig(requestOptions, token));
        return this.runFnWithToken(postFn);
    }
    patch(path, body, options = {}) {
        const requestOptions = { ...defaultRequestOptions, ...options };
        const requestPath = this.versionType === VersionType.URI ? this.versionUrl(requestOptions.version, path) : path;
        const patchFn = (token) => this.axiosInstance.patch(requestPath, body, this.buildConfig(requestOptions, token));
        return this.runFnWithToken(patchFn);
    }
    delete(path, options = {}) {
        const requestOptions = { ...defaultRequestOptions, ...options };
        const requestPath = this.versionType === VersionType.URI ? this.versionUrl(requestOptions.version, path) : path;
        const deleteFn = (token) => this.axiosInstance.delete(requestPath, this.buildConfig(requestOptions, token));
        return this.runFnWithToken(deleteFn);
    }
    uploadFile(path, formData, options = {}) {
        const requestOptions = { ...defaultRequestOptions, ...options, contentType: 'multipart/form-data' };
        const requestPath = this.versionType === VersionType.URI ? this.versionUrl(requestOptions.version, path) : path;
        const postFn = (token) => this.axiosInstance.post(requestPath, formData, this.buildConfig(requestOptions, token));
        return this.runFnWithToken(postFn);
    }
    versionUrl(version, path) {
        const versionTag = `v${version}`;
        if (path.startsWith('/')) {
            return `${versionTag}${path}`;
        }
        else {
            return `${versionTag}/${path}`;
        }
    }
    runFnWithToken(fn) {
        if (isObservable(this.token$)) {
            return this.token$.pipe(switchMap(fn));
        }
        else {
            return from(fn(this.token$()));
        }
    }
    buildConfig(options, token) {
        return {
            ...this.config,
            responseType: options.responseType,
            headers: { ...this.buildHeaders(options.version, token, options.contentType), ...(options.headers ?? {}) },
        };
    }
    buildHeaders(version, token, contentType) {
        const headers = this.versionType === VersionType.Header ? { 'Accept-Version': version } : {};
        if (contentType) {
            headers['Content-Type'] = contentType;
        }
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { RestApiClient, VersionType };
